import { useEffect } from 'react'
import { usePearl } from '../lib/pearlchat'
import { PearlView } from './Pearl'

/**
 * Pearl, beside the work rather than instead of it.
 *
 * A TRUE SIDEBAR, DECIDED 2026-08-21
 *
 * This used to float over the page (`position: fixed`, on the right, above
 * whatever was underneath) rather than take real layout space — the
 * reasoning at the time was that the agent list's fixed 330px column beside
 * a detail pane would rewrap and reflow, moving the thing she was pointing
 * at right as she asked about it. Chris's own call overturned that: he wants
 * Pearl to behave like the rest of this app's own panels — collapsing and
 * resizing the work area beside it, not sitting on top of it. This
 * component no longer positions itself at all; it is a plain flex child,
 * and `App.tsx`'s `.shell-body`/`.shell-main` split is what actually makes
 * room for it (see the CSS comment there for the `min-width: 0` mechanics
 * that make the roster's own grid willing to shrink).
 *
 * WHY THERE IS STILL NO BACKDROP
 *
 * A modal dims what is behind it and swallows clicks, and both would be
 * wrong here whether the panel overlays or sits beside — the entire point
 * is that she works the screen WHILE Pearl is open, following the steps,
 * pressing the buttons, watching what happens. Nothing beside this is
 * disabled, and now nothing is even underneath it to dim.
 *
 * IT IS NOT MOUNTED WHEN CLOSED, and that is safe in a way it was not
 * before the conversation moved above the routes: it lives in the provider,
 * so unmounting the panel puts nothing at risk, and it is also what makes
 * "closed" give the work area its width straight back rather than leaving a
 * collapsed-but-present sliver. Keeping a hidden copy mounted would mean two
 * Pearl views alive at once, both scrolling themselves and both grabbing
 * focus when a reply lands.
 */
export default function PearlPanel() {
  const { panelOpen, closePanel } = usePearl()

  /**
   * Escape closes it.
   *
   * Attached only while it is open, so nothing about this page listens for a
   * keystroke it has no use for — and, more to the point, so Escape keeps
   * meaning whatever it means to the menus and dialogs on the screen behind
   * when Pearl is not up.
   */
  useEffect(() => {
    if (!panelOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [panelOpen, closePanel])

  if (!panelOpen) return null

  return (
    <aside className="pearlpanel" aria-label="Ask Pearl">
      <div className="pearlpanel-head">
        <strong>Pearl</strong>
        <span className="hint">Ask about this screen — it stays open while you work.</span>
        <button className="btn ghost sm" onClick={closePanel} aria-label="Close Pearl">
          Close
        </button>
      </div>
      <PearlView dense />
    </aside>
  )
}
